import React, { useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../../../components/Modal';
import Button from '../../../components/Button';
import MapProductContent from '../uploadStock/components/MapProductContent';
import MonthlyStockService from '../../../services/monthlyStock.service';

interface StockMappingModalProps {
  open: boolean;
  monthlyStockId: number;
  stockistProductId: number | null;
  stockistProductName: string;
  initialDivisionId?: string;
  initialProductId?: string;
  onClose: () => void;
  onMapped: () => void;
}

const StockMappingModal: React.FC<StockMappingModalProps> = ({
  open,
  monthlyStockId,
  stockistProductId,
  stockistProductName,
  initialDivisionId = '',
  initialProductId = '',
  onClose,
  onMapped,
}) => {
  const { t } = useTranslation();
  const isRemap = !!initialProductId;

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ division: initialDivisionId, product: initialProductId });
  const [validation, setValidation] = useState<{ division?: string; product?: string }>({});

  const formDataRef = useRef(formData);
  formDataRef.current = formData;

  const handleFormChange = useCallback((data: { division: string; product: string }) => {
    setFormData(data);
    setValidation((prev) => ({
      division: data.division ? undefined : prev.division,
      product: data.product ? undefined : prev.product,
    }));
  }, []);

  const handleClose = useCallback(() => {
    setFormData({ division: initialDivisionId, product: initialProductId });
    setValidation({});
    onClose();
  }, [onClose, initialDivisionId, initialProductId]);

  const handleSubmit = useCallback(async () => {
    const current = formDataRef.current;
    const errors: { division?: string; product?: string } = {};
    if (!current.division) errors.division = t('monthlyStock.addEdit.pageTexts.verifyData.table.mapModal.errors.divisionRequired');
    if (!current.product) errors.product = t('monthlyStock.addEdit.pageTexts.verifyData.table.mapModal.errors.productRequired');

    if (Object.keys(errors).length > 0) {
      setValidation(errors);
      return;
    }

    try {
      setLoading(true);
      await MonthlyStockService.mapStockistProduct(monthlyStockId, {
        stockist_product_id: stockistProductId!,
        product_id: Number(current.product),
        force: isRemap,
      });
      setFormData({ division: '', product: '' });
      setValidation({});
      onMapped();
    } catch {
      // handled by interceptor
    } finally {
      setLoading(false);
    }
  }, [monthlyStockId, stockistProductId, isRemap, onMapped, t]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={t('monthlyStock.addEdit.pageTexts.verifyData.title')}
      titleClassName="text-start"
      content={
        <MapProductContent
          stockistProduct={stockistProductName}
          initialDivisionId={initialDivisionId}
          initialProductId={initialProductId}
          onFormChange={handleFormChange}
          validation={validation}
        />
      }
      actionClassName="justify-content-start"
      action={
        <>
          <Button
            className="btn btn-primary waves-effect waves-light"
            onClick={handleSubmit}
            text={t('monthlyStock.addEdit.pageTexts.verifyData.table.mapModal.mapButton')}
            loading={loading}
          />
          <Button
            className="btn btn-outline-secondary waves-effect"
            onClick={handleClose}
            text={t('monthlyStock.addEdit.pageTexts.verifyData.buttons.cancel')}
            disabled={loading}
          />
        </>
      }
      maxWidth="sm"
    />
  );
};

export default StockMappingModal;
